import React, { Fragment, PureComponent, ReactNode, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';

import { ThunkDispatch } from 'redux-thunk';
import { GlobalState } from '../store/reducers';
import { Actions } from '../store/actions';

import { DocumentsEntity, NO_ACCESS } from '../api/fetchMedicalRecords';
import { AnimatePresence, motion } from 'framer-motion';
import initiateFileUpload from '../api/initiateFileUpload';
import MedicalRecordFileTypeSelector, {
    OPTIONS,
    SuggestionItem,
} from '../components/common/MedicalRecordFileTypeSelector';
import { track } from '../tracking';
import { GMedicalRecordsTags, LabVitalEntity } from '../store/types/prescription';
import { DateRange, FilterDialogV2 } from '../components/paymentsDashboard/FiltersDialogV2';
import { getGenieSetting, getIsStorageInfo, getPxElementConfig } from '../store/selectors/session';
import { Encounter, fetchMR, onMedicalRecordDelete } from './utils';
import ModalDialog from '../components/common/ModalDialog';
import CopyToAll from '../components/prescriptionPad/rightPanel/workspace/padV2/CopyToAll';
import { Checkbox, Select, notification } from 'antd';
import { uploadIcon } from '../components/common/Icons';
import { AntTooltip } from '../components/common/AntTooltip';
import RequestMedicalRecords from './RequestMedicalRecords';
import {
    getDoc,
    getDoctorSelectorList,
    getGArray,
    getPatientByLocalId,
    getSelectedClinicId,
    updateG,
} from '../store/idbHandler';
import fetchGovernmentMedicalRecords, { PatientRecordType } from '../api/NHDMConsentList';
import { requestMedicalRecords } from '../api/requestMedicalRecords';
import { captureError } from './captureError';
import SwitchPanel, { ACTIVE_TAB, DICOM, MY_RECORDS, REQUEST } from './MedicalRecordsSwitchPanel';
import { Patient } from '../store/types/patient';
import { StorageInfoResponse } from '../api/fetchStorageInfo';
import { AllOptions } from '../components/prescriptionPad/rightPanel/workspace/padV2/PadV2AutoSuggest';
import debounce from 'lodash/debounce';
import { LOCAL_ID_PREFIX } from '../constants';
import { hashCode, randomString } from '../store/utils';
import { pushGenieToBE } from '../store/appReduxMiddleware';
import { DB_TABLES } from '../store/dbTables';
import { LABEL_TYPE, OPTION_SOURCE } from '../components/prescriptionPad/trackSource';
import MedicalRecordNew from './MedicalRecordNew';
import uniq from 'lodash/uniq';
import ABDM from '../components/mobilenumber/ABDMEnroll';
import { MedicalRecordsTableOld } from './MedicalRecordsTableOld';
import { getMActiveId } from '../store/localstorage';
import DicomContainer from './DicomContainer';
import { getAclViewClassName } from '../restrictions/utils';
import { SREV2 } from '../api/pushGenie';
import pollMedicalRecords from '../api/pollMedicalRecords';
import { formatDistanceToNow } from 'date-fns';
import MedicalRecordLeftPanel from './MedicalRecordLeftPanel';
import MedicalRecordsVisitHeader from './MedicalRecordsVisitHeader';

const uploadMRIcon = uploadIcon('fill-white');

export const SUPPORTED_MR_FORMATS =
    'image/jpeg, image/png, application/pdf, audio/aac, audio/mpeg, audio/ogg, audio/wav, video/mp4, video/x-msvideo, video/3gpp, video/mpeg';

const IMAGE_TYPE = ['image/jpeg', 'image/png'];
const AUDIO_TYPE = ['audio/aac', 'audio/mpeg', 'audio/ogg', 'audio/wav'];
const VIDEO_TYPE = ['video/mp4', 'video/x-msvideo', 'video/3gpp', 'video/mpeg'];

export const MEDICAL_RECORDS_AS_EL_ID = ' MEDICAL_RECORDS_AS_EL_ID';
export const MEDICAL_RECORDS_AS_INPUT = ' MEDICAL_RECORDS_AS_INPUT';
const TAGS_ID = 'TAGS_ID';

interface Props {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    plId: string;
    setMedicalRecordsCount?: (count: number) => void;
    copyToPrescription?: (items: LabVitalEntity[]) => void;
    isLabVitalsEnabled?: boolean;
    isABDM?: boolean;
    storageInfo?: StorageInfoResponse;
    activeTab?: ACTIVE_TAB | undefined;
    background?: string;
    hideSwitchPanel?: boolean;
    hideABHACta?: boolean;
    hideCopyToPrescription?: boolean;
}

export type UIDocumentEntity = DocumentsEntity & { _pt_oid: string; enc_id?: string };

const ACTIVE_MR_TAB_IN_PD = 'active-mr-section-in-pd';
type FETCH_STATE = 'loading' | 'error' | 'success' | typeof NO_ACCESS;
type PROFILE_FILTER = 'profile' | 'all';

type MedicalRecordsTypeOptions = AllOptions<GMedicalRecordsWithTrack>;

export type GMedicalRecordsWithTrack = {
    id: string;
    name: string;
    track?: {
        label: LABEL_TYPE;
        index: number;
        source: OPTION_SOURCE;
    };
};

type State = {
    medicalRecords?: UIDocumentEntity[];

    fetchState: FETCH_STATE;

    selectedTabIndex: number;
    isFilterDialogVisible?: boolean;
    isFilesInDocLoading?: boolean;

    profileFilterValue: PROFILE_FILTER;
    ptIds?: Set<string>;
    activeTab: ACTIVE_TAB;
    isModalOpen: boolean;
    patientDirectory: Patient | undefined;
    isRequestLoading?: boolean;
    patientRecords: PatientRecordType[];
    tags?: Tags;
    activeTags?: Tags;
    recordUploaded?: DateRange;
    documentDate?: DateRange;
    isMrLoading?: boolean;
    latestUpdatedAt?: string;
    withRefresh?: boolean;
    sourceRefreshedAt?: number;
    encounters?: Encounter[];
    selectedEncounter?: Encounter;
};

export type Tags = {
    [key: string]: number;
};

const initialStateForMyRequestDeets = {
    fetchState: 'loading' as FETCH_STATE,
    selectedTabIndex: 0,

    profileFilterValue: 'profile' as PROFILE_FILTER,
    tags: undefined,
    activeTags: undefined,
    isMrLoading: false,
};

class MedicalRecordsRightPanel extends PureComponent<Props, State> {
    fetchMedicalRecordsBasedOnSearchDebounce: (input: string) => void;

    constructor(props: Props) {
        super(props);

        this.fetchMedicalRecordsBasedOnSearchDebounce = debounce(
            this.fetchMedicalRecordsBasedOnSearch,
            400,
        );
    }

    state: State = {
        ...initialStateForMyRequestDeets,
        activeTab: this.props?.activeTab ?? this.getMRActiveTab(),
        isModalOpen: false,
        patientDirectory: undefined,
        patientRecords: [],
        encounters: undefined,
        selectedEncounter: undefined,
    };
    polling?: NodeJS.Timeout = undefined;
    POLL_INTERVAL_MS = 5000;
    POLL_COUNT = 0;

    doctorprofile = getDoc();

    getMRActiveTab() {
        return (localStorage.getItem(ACTIVE_MR_TAB_IN_PD) as ACTIVE_TAB) || MY_RECORDS;
    }
    tabs = [
        {
            label: 'All Files',
            filter: (list: UIDocumentEntity[]): UIDocumentEntity[] => {
                return list;
            },
        },
        {
            label: 'Uploaded by You',
            filter: (list: UIDocumentEntity[]): UIDocumentEntity[] => {
                return list.filter((d) => d.uploaded_by_me);
            },
        },
    ];

    toggleFilterDialog = (visible?: boolean) => {
        this.setState({ isFilterDialogVisible: visible });
    };

    componentWillUnmount() {
        window.onafterprint = () => {
            return;
        };
        localStorage.removeItem(ACTIVE_MR_TAB_IN_PD);
        if (this.polling) {
            track('MR_REFRESH_POLL', {
                status: 'component unmount',
                pollCount: String(this.POLL_COUNT),
            });
        }
        clearInterval(this.polling);
        this.POLL_COUNT = 0;
    }

    componentDidMount = async (): Promise<void> => {
        this.setActiveTab(this.props?.activeTab ?? this.getMRActiveTab());
        this.fetchPatientDirectory();
    };

    poll = async (sourceRefreshedAt?: number) => {
        const { plId } = this.props;
        this.POLL_COUNT++;
        try {
            const { refresh_available } = await pollMedicalRecords(plId, sourceRefreshedAt);
            if (refresh_available) {
                track('MR_REFRESH_POLL', {
                    status: 'refresh available',
                    pollCount: String(this.POLL_COUNT),
                });
                clearInterval(this.polling);
                this.POLL_COUNT = 0;
                this.fetchDeets();
                this.setState({
                    withRefresh: false,
                });
            }
        } catch (err) {
            captureError(err as Error);
            track('ERROR', {
                on: 'pollMedicalRecords',
                error: (err as Error).message,
            });
            track('MR_REFRESH_POLL', {
                status: 'refresh error',
                pollCount: String(this.POLL_COUNT),
            });
            clearInterval(this.polling);
            this.POLL_COUNT = 0;
            this.setState({
                withRefresh: false,
            });
        }
    };

    setMedicalRecordsTags = (
        profileFilterValue: PROFILE_FILTER,
        selectedTabIndex: number,
        medicalRecords?: UIDocumentEntity[],
        ptIds?: Set<string>,
    ) => {
        this.setState({ activeTags: undefined });

        if (profileFilterValue === 'profile') {
            const medicalRecordsData =
                medicalRecords && this.tabs[selectedTabIndex]
                    ? this.tabs[selectedTabIndex].filter(medicalRecords)
                    : [];
            const patientMrRecords = medicalRecordsData.filter((item) =>
                ptIds?.has(item.oid || ''),
            );

            this.buildTags(patientMrRecords);
        } else {
            const medicalRecordsData =
                medicalRecords && this.tabs[selectedTabIndex]
                    ? this.tabs[selectedTabIndex].filter(medicalRecords)
                    : [];
            this.buildTags(medicalRecordsData);
        }
    };

    buildTags = (medicalRecords?: UIDocumentEntity[]) => {
        const allTags = {} as Tags;

        medicalRecords?.forEach((mr) => {
            if (mr.latest_document_type && !mr.user_tags?.find((tag) => tag.startsWith('RT#'))) {
                allTags[mr.latest_document_type] = (allTags[mr.latest_document_type] || 0) + 1;
            }

            mr?.user_tags?.forEach((mrEle) => {
                if (mrEle) {
                    allTags[mrEle] = (allTags[mrEle] || 0) + 1;
                }
            });
        });

        const finalObj = {} as Tags;

        Object.keys(allTags).forEach((key) => {
            if (OPTIONS.find((option) => option.value === key)) {
                finalObj[key] = allTags[key];
            } else if (key?.startsWith('RT#')) {
                finalObj[key] = allTags[key];
            }
        });

        Object.keys(allTags).forEach((key) => {
            if (!OPTIONS.find((option) => option.value === key) && !key?.startsWith('RT#')) {
                finalObj[key] = allTags[key];
            }
        });

        this.setState({ tags: finalObj });
    };

    fetchDeets = async (withRefresh?: boolean) => {
        this.setState({
            ...initialStateForMyRequestDeets,
            selectedTabIndex: this.state.selectedTabIndex,
        });

        const { plId } = this.props;

        if (!plId) {
            return;
        }

        const data = await fetchMR(plId, undefined, withRefresh);

        if (!data) {
            return;
        }

        this.setMedicalRecordsTags(
            this.state.profileFilterValue,
            this.state.selectedTabIndex,
            data.medicalRecords,
            new Set(data.allPtIds),
        );

        if (data.fetchState === 'success') {
            const latestDocument = data?.medicalRecords?.[0];
            const latestUpdatedAt = latestDocument?.display_dd;
            const sourceRefreshedAt = data.sourceRefreshedAt;
            this.setState({
                medicalRecords: data.medicalRecords,
                fetchState: data.fetchState,
                ptIds: new Set(data.allPtIds),
                latestUpdatedAt: latestUpdatedAt,
                sourceRefreshedAt: data.sourceRefreshedAt,
                encounters: data.encounters,
            });
            if (withRefresh) {
                if (data.poll) {
                    clearInterval(this.polling);
                    this.POLL_COUNT = 0;
                    this.poll(sourceRefreshedAt);
                    this.polling = setInterval(
                        () => this.poll(sourceRefreshedAt),
                        this.POLL_INTERVAL_MS,
                    );
                } else {
                    this.setState({
                        withRefresh: false,
                    });
                }
            }
            this.props.setMedicalRecordsCount?.(
                data.medicalRecords ? data.medicalRecords.length : 0,
            );
            return;
        }

        if (data.fetchState === 'error') {
            this.setState({
                fetchState: data.fetchState,
                ptIds: new Set(data.allPtIds),
            });
            if (withRefresh) {
                clearInterval(this.polling);
                this.POLL_COUNT = 0;
                this.setState({
                    withRefresh: false,
                });
            }
            alert('Failed to fetch medical records');
            return;
        }

        if (data.fetchState === NO_ACCESS) {
            this.setState({
                fetchState: data.fetchState,
                ptIds: new Set(data.allPtIds),
            });
            track('ERROR', { error: NO_ACCESS, on: `fetching MR for ${data.allPtIds.toString()}` });
            return;
        }
    };

    fetchMedicalRecordsBasedOnSearch = async (search_term?: string) => {
        this.setState({ isMrLoading: true });
        const { plId } = this.props;

        if (!plId) {
            return;
        }

        const data = await fetchMR(plId, search_term?.length === 1 ? undefined : search_term);

        this.setState({ isMrLoading: false });

        if (!data) {
            return;
        }

        this.setMedicalRecordsTags(
            this.state.profileFilterValue,
            this.state.selectedTabIndex,
            data.medicalRecords,
            new Set(data.allPtIds),
        );

        if (data.fetchState === 'success') {
            const latestDocument = data?.medicalRecords?.[0];
            const latestUpdatedAt = latestDocument?.display_dd;
            this.setState({
                medicalRecords: data.medicalRecords,
                fetchState: data.fetchState,
                ptIds: new Set(data.allPtIds),
                latestUpdatedAt: latestUpdatedAt,
                sourceRefreshedAt: data.sourceRefreshedAt,
            });
            this.props.setMedicalRecordsCount?.(
                data.medicalRecords ? data.medicalRecords.length : 0,
            );
            return;
        }

        if (data.fetchState === 'error') {
            this.setState({
                fetchState: data.fetchState,
                ptIds: new Set(data.allPtIds),
            });
            alert('Failed to fetch medical records');
            return;
        }

        if (data.fetchState === NO_ACCESS) {
            this.setState({
                fetchState: data.fetchState,
                ptIds: new Set(data.allPtIds),
            });
            track('ERROR', { error: NO_ACCESS, on: `fetching MR for ${data.allPtIds.toString()}` });
            return;
        }
    };

    onRefresh = () => {
        if (this.getMRActiveTab() === REQUEST) {
            this.fetchConsentList();
        } else {
            this.setState({ withRefresh: true });
            this.fetchDeets(true);
        }
        track('MR_REFRESH_CLICK', { activeTab: this.getMRActiveTab() });
    };

    fetchPatientDirectory = async () => {
        const patientDirectory = await getPatientByLocalId(this.props.plId);
        this.setState({ patientDirectory: patientDirectory });
    };

    copyToPrescription =
        this.props.copyToPrescription && this.props.isLabVitalsEnabled
            ? this.props.copyToPrescription
            : undefined;

    onRecordDelete = async (doc: UIDocumentEntity) => {
        try {
            await onMedicalRecordDelete(doc.document_id, doc._pt_oid);
            this.fetchDeets();
        } catch (e) {
            alert('Failed to delete record');
        }
    };

    onProfileFilterChange = (value: 'profile' | 'all') => {
        this.setMedicalRecordsTags(
            value,
            this.state.selectedTabIndex,
            this.state.medicalRecords,
            this.state.ptIds,
        );
        this.setState({ profileFilterValue: value });
        return;
    };

    setActiveTab = (activeTab: ACTIVE_TAB) => {
        this.setState({ activeTab: activeTab });
        localStorage.setItem(ACTIVE_MR_TAB_IN_PD, activeTab);

        if (activeTab === REQUEST) {
            this.fetchConsentList();
        } else {
            this.fetchDeets();
        }
    };

    openRequestMedicalRecords = () => {
        this.setState({ isModalOpen: true });
    };

    requestMedicalRecords = () => {
        const { plId, isABDM } = this.props;
        const { patientDirectory } = this.state;

        track('REQUEST_MEDICAL_RECORDS_RP', {
            isABDM: isABDM?.toString(),
            consentRequest: (patientDirectory?.abha && patientDirectory.abha?.id)?.toString(),
        });

        if (isABDM === false) {
            requestMedicalRecords(getDoctorSelectorList()?.[0].id, plId);
            return;
        }

        if (patientDirectory?.abha && patientDirectory.abha?.id) {
            this.openRequestMedicalRecords();
            return;
        }

        notification.error({
            message: 'Patient Health Id not found. Please try again or refresh!',
            duration: 5,
            placement: 'topRight',
        });
    };

    closeRequestMedicalRecords = () => {
        this.setState({ isModalOpen: false });
    };

    setActiveTags = (name: string) => {
        this.setState({
            activeTags: {
                ...this.state.activeTags,
                [name]: 1,
            },
        });
    };

    onApplyFilter = (
        recordUploaded?: DateRange,
        documentDate?: DateRange,
        activeTags?: Tags,
        selectedTabIndex?: number,
    ) => {
        this.setState({
            recordUploaded: recordUploaded,
            documentDate: documentDate,
            activeTags: activeTags,
            selectedTabIndex:
                selectedTabIndex === undefined ? this.state.selectedTabIndex : selectedTabIndex,
        });
    };

    removeActiveTags = (name: string) => {
        const { activeTags } = this.state;
        const tags = { ...activeTags };
        delete tags?.[name];

        this.setState({
            activeTags: tags,
        });
    };

    onClickScrollHorizontal = (direction: 'left' | 'right') => {
        const scrollAmount = direction === 'left' ? -100 : 100;
        const scrollContainer = document.getElementById(TAGS_ID);
        scrollContainer?.scrollBy({
            top: 0,
            left: scrollAmount,
            behavior: 'smooth',
        });
    };

    fetchConsentList = async () => {
        const { plId } = this.props;
        this.setState({ isRequestLoading: true });
        const doc = getDoc();
        const patientDirectory = await getPatientByLocalId(plId);
        const clinicId = getSelectedClinicId();
        const data = {
            patient: {
                oid: patientDirectory?.id || '',
                health_id: patientDirectory.abha?.id || '',
            },
            hiu: {
                clinic_id: clinicId || '',
                d_oid: doc?.loginmeta?.docid || '',
            },
        };
        try {
            const patientRecords = await fetchGovernmentMedicalRecords(data);
            this.setState({ patientRecords: patientRecords.consents, isRequestLoading: false });
        } catch (error) {
            this.setState({ isRequestLoading: false });
            captureError(error as Error);
            console.log(error);
        }
    };

    render(): ReactNode {
        const {
            medicalRecords,
            fetchState,
            selectedTabIndex: seletedTabIndex,
            isFilterDialogVisible,
            profileFilterValue,
            ptIds,
            activeTab,
            isModalOpen,
            patientDirectory,
            tags,
            activeTags,
            documentDate,
            recordUploaded,
            isMrLoading,
            selectedTabIndex,
            isRequestLoading,
            patientRecords,
            latestUpdatedAt,
            withRefresh,
            sourceRefreshedAt,
            encounters,
            selectedEncounter,
        } = this.state;

        const {
            plId,
            isABDM,
            background,
            hideSwitchPanel,
            hideABHACta,
            hideCopyToPrescription,
        } = this.props;

        const isFilterApplied =
            Object.keys(recordUploaded || {})?.length ||
            Object.keys(documentDate || {})?.length ||
            Object.keys(activeTags || {})?.length ||
            selectedTabIndex;

        const medicalRecordsData =
            medicalRecords && this.tabs[seletedTabIndex]
                ? this.tabs[seletedTabIndex].filter(medicalRecords)
                : [];

        const mrList = medicalRecordsData.filter((mr) => {
            if (
                mr.document_date &&
                documentDate &&
                !moment(mr.document_date).isBetween(
                    documentDate?.startDate,
                    documentDate?.endDate,
                    null,
                    '[]',
                )
            ) {
                return false;
            }

            if (
                mr.u_at &&
                recordUploaded &&
                !moment(mr.document_date).isBetween(
                    recordUploaded?.startDate,
                    recordUploaded?.endDate,
                    null,
                    '[]',
                )
            ) {
                return false;
            }

            if (
                Object.keys(activeTags || {})?.length &&
                !(
                    mr?.user_tags?.find((key) => activeTags?.[key]) ||
                    activeTags?.[mr?.latest_document_type || '']
                )
            ) {
                return false;
            }

            const { selectedEncounter } = this.state;

            if (selectedEncounter && selectedEncounter.id !== mr.enc_id) return false;

            return true;
        });

        let list = mrList;
        let diffCount = 0;

        if (profileFilterValue === 'profile') {
            list = list.filter((item) => ptIds?.has(item.oid || ''));
            diffCount =
                medicalRecordsData.length -
                medicalRecordsData.filter((item) => ptIds?.has(item.oid || '')).length;
        } else {
            diffCount =
                medicalRecordsData.length -
                medicalRecordsData.filter((item) => ptIds?.has(item.oid || '')).length;
        }

        if (
            !mrList ||
            mrList.length === 0 ||
            fetchState === 'error' ||
            fetchState === NO_ACCESS ||
            !list.length
        ) {
            return (
                <div
                    onClick={() => this.toggleFilterDialog(false)}
                    className="flex flex-col h-full col-span-10 overflow-hidden bg-neutral-8 relative w-full"
                    style={{
                        background: background || '#F4F4F4',
                    }}
                >
                    <div>
                        <NavBar
                            tabs={this.tabs}
                            plId={plId}
                            onRefresh={this.onRefresh}
                            onProfileFilterChange={this.onProfileFilterChange}
                            profileFilter={profileFilterValue}
                            diffCount={diffCount}
                            onRequest={this.requestMedicalRecords}
                            isABDM={isABDM}
                            latestUpdatedAt={latestUpdatedAt}
                            withRefresh={withRefresh}
                            sourceRefreshedAt={sourceRefreshedAt}
                            hideABHACta={hideABHACta}
                        />
                        {!hideSwitchPanel && (
                            <SwitchPanel
                                activeTab={activeTab}
                                setActiveTab={this.setActiveTab}
                                storageInfo={this.props.storageInfo}
                            />
                        )}
                    </div>

                    {activeTab === MY_RECORDS && fetchState === 'loading' ? (
                        <div className="flex items-center justify-center h-full w-full">
                            <div className="text-neutral-3 text-16 font-500 ai_patient_summary_loading">
                                Fetching medical records
                            </div>
                        </div>
                    ) : (
                        activeTab === MY_RECORDS && (
                            <>
                                <div className="flex p-16 w-full space-x-16 w-full items-center">
                                    <div className="relative flex justify-end items-center gap-8">
                                        <div
                                            style={{ width: '270px' }}
                                            className="medical-records flex w-300 bg-white h-36 border gap-8 border-darwin-neutral_new-200 rounded-8 items-center justify-between px-12 py-6 leading-5 bg-transparent"
                                        >
                                            {isMrLoading ? (
                                                <svg
                                                    className="h-16 fill-darwin-accent-bright-blue-800 animate-spin"
                                                    style={{
                                                        width: '17px',
                                                    }}
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 512 512"
                                                >
                                                    <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                                </svg>
                                            ) : (
                                                <svg
                                                    width="17"
                                                    height="16"
                                                    viewBox="0 0 17 16"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M15.75 14.7188C16.0625 15.0312 16.0625 15.5 15.75 15.7812C15.625 15.9375 15.4375 16 15.25 16C15.0312 16 14.8438 15.9375 14.6875 15.7812L10.5 11.5938C9.375 12.5 7.96875 13 6.46875 13C2.90625 13 0 10.0938 0 6.5C0 2.9375 2.875 0 6.46875 0C10.0312 0 12.9688 2.9375 12.9688 6.5C12.9688 8.03125 12.4688 9.4375 11.5625 10.5312L15.75 14.7188ZM1.5 6.5C1.5 9.28125 3.71875 11.5 6.5 11.5C9.25 11.5 11.5 9.28125 11.5 6.5C11.5 3.75 9.25 1.5 6.5 1.5C3.71875 1.5 1.5 3.75 1.5 6.5Z"
                                                        fill="#767676"
                                                    />
                                                </svg>
                                            )}

                                            <input
                                                type="search"
                                                className="w-full bg-transparent bg-white focus:outline-none placeholder-neutral-5 text-14"
                                                name="ether-search-component"
                                                autoComplete="off"
                                                placeholder="Search In Records"
                                                onChange={(e) =>
                                                    this.fetchMedicalRecordsBasedOnSearchDebounce(
                                                        e.target.value,
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                    <div className="h-30 w-1 bg-darwin-neutral-500"></div>
                                    <div style={{marginLeft: 0}} className="flex items-center overflow-hidden hide-scrollbar w-full">
                                        <div className="flex items-center border-r border-light">
                                            <div
                                                className="px-10 cursor-pointer "
                                                onClick={() => this.onClickScrollHorizontal('left')}
                                                style={{ transform: 'rotate(180deg)' }}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="8"
                                                    height="14"
                                                    viewBox="0 0 8 14"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M7.4082 6.46875C7.68945 6.78125 7.68945 7.25 7.4082 7.53125L1.4082 13.5312C1.0957 13.8438 0.626953 13.8438 0.345703 13.5312C0.0332031 13.25 0.0332031 12.7812 0.345703 12.5L5.81445 7.03125L0.345703 1.53125C0.0332031 1.25 0.0332031 0.78125 0.345703 0.5C0.626953 0.1875 1.0957 0.1875 1.37695 0.5L7.4082 6.46875Z"
                                                        fill="#484848"
                                                    />
                                                </svg>
                                            </div>
                                        </div>
                                        <div
                                            id={TAGS_ID}
                                            className="flex items-center space-x-8 overflow-x-scroll hide-scrollbar w-full"
                                        >
                                            {Object.keys(tags || {})?.map((key) => {
                                                const isSelected = activeTags?.[key];

                                                return (
                                                    <div
                                                        className={`${
                                                            isSelected
                                                                ? 'font-700 bg-darwin-accent-bright-blue-100 text-darwin-accent-bright-blue-800 border-darwin-accent-bright-blue-800'
                                                                : 'bg-white text-darwin-neutral-1000 border-darwin-neutral_new-200'
                                                        } flex items-center space-x-8 whitespace-nowrap text-13 px-12 py-8 rounded-8 border cursor-pointer`}
                                                        onClick={() => this.setActiveTags(key)}
                                                    >
                                                        <span>
                                                            {(
                                                                OPTIONS?.find(
                                                                    (o) => o.value === key,
                                                                )?.label || key
                                                            )?.replace(/^RT#/, '')}{' '}
                                                            ({tags?.[key]})
                                                        </span>
                                                        {isSelected && (
                                                            <span
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    this.removeActiveTags(key);
                                                                }}
                                                            >
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width="11"
                                                                    height="12"
                                                                    viewBox="0 0 11 12"
                                                                    fill="none"
                                                                >
                                                                    <path
                                                                        d="M10.6582 2.28125L6.9082 6.03125L10.627 9.75C10.9395 10.0312 10.9395 10.5 10.627 10.7812C10.3457 11.0938 9.87695 11.0938 9.5957 10.7812L5.8457 7.0625L2.12695 10.7812C1.8457 11.0938 1.37695 11.0938 1.0957 10.7812C0.783203 10.5 0.783203 10.0312 1.0957 9.71875L4.81445 6L1.0957 2.28125C0.783203 2 0.783203 1.53125 1.0957 1.21875C1.37695 0.9375 1.8457 0.9375 2.1582 1.21875L5.87695 4.96875L9.5957 1.25C9.87695 0.9375 10.3457 0.9375 10.6582 1.25C10.9395 1.53125 10.9395 2 10.6582 2.28125Z"
                                                                        fill="#1A1A1A"
                                                                    />
                                                                </svg>
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <div className="flex items-center border-l border-light">
                                            <div
                                                className="px-10 cursor-pointer"
                                                onClick={() => this.onClickScrollHorizontal('right')}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="8"
                                                    height="14"
                                                    viewBox="0 0 8 14"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M7.4082 6.46875C7.68945 6.78125 7.68945 7.25 7.4082 7.53125L1.4082 13.5312C1.0957 13.8438 0.626953 13.8438 0.345703 13.5312C0.0332031 13.25 0.0332031 12.7812 0.345703 12.5L5.81445 7.03125L0.345703 1.53125C0.0332031 1.25 0.0332031 0.78125 0.345703 0.5C0.626953 0.1875 1.0957 0.1875 1.37695 0.5L7.4082 6.46875Z"
                                                        fill="#484848"
                                                    />
                                                </svg>
                                            </div>
                                            <div
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    this.toggleFilterDialog(true);
                                                }}
                                                className={`flex items-center justify-end space-x-8 cursor-pointer text-13 px-12 py-8 rounded-8 bg-white font-700 ${
                                                    isFilterApplied
                                                        ? 'bg-darwin-accent-bright-blue-100 border border-darwin-accent-bright-blue-800'
                                                        : 'border border-darwin-neutral_new-200'
                                                }`}
                                            >
                                                <div
                                                    className={`w-16 h-16 flex items-center justify-center`}
                                                >
                                                    {isFilterApplied ? (
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 512 512"
                                                            className="fill-darwin-accent-bright-blue-800"
                                                        >
                                                            <path d="M3.9 54.9C10.5 40.9 24.5 32 40 32H472c15.5 0 29.5 8.9 36.1 22.9s4.6 30.5-5.2 42.5L320 320.9V448c0 12.1-6.8 23.2-17.7 28.6s-23.8 4.3-33.5-3l-64-48c-8.1-6-12.8-15.5-12.8-25.6V320.9L9 97.3C-.7 85.4-2.8 68.8 3.9 54.9z" />
                                                        </svg>
                                                    ) : (
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 512 512"
                                                            className="fill-darwin-accent-bright-blue-800 w-16 h-16"
                                                        >
                                                            <path d="M0 73.7C0 50.67 18.67 32 41.7 32H470.3C493.3 32 512 50.67 512 73.7C512 83.3 508.7 92.6 502.6 100L336 304.5V447.7C336 465.5 321.5 480 303.7 480C296.4 480 289.3 477.5 283.6 472.1L191.1 399.6C181.6 392 176 380.5 176 368.3V304.5L9.373 100C3.311 92.6 0 83.3 0 73.7V73.7zM54.96 80L218.6 280.8C222.1 285.1 224 290.5 224 296V364.4L288 415.2V296C288 290.5 289.9 285.1 293.4 280.8L457 80H54.96z"></path>
                                                        </svg>
                                                    )}
                                                </div>
                                                <p className="body-1 text-darwin-accent-bright-blue-800">
                                                    Filters
                                                </p>

                                                {isFilterDialogVisible && (
                                                    <ModalDialog>
                                                        <motion.div
                                                            initial={{ opacity: 0 }}
                                                            exit={{ opacity: 0 }}
                                                            animate={{ opacity: 1 }}
                                                            style={{ zIndex: 9999 }}
                                                            transition={{
                                                                duration: 0.2,
                                                                ease: 'linear',
                                                            }}
                                                            className="font-lato select-none"
                                                        >
                                                            <FilterDialogV2
                                                                activeTags={activeTags}
                                                                tags={tags}
                                                                tabs={this.tabs}
                                                                selectedTabIndex={
                                                                    this.state.selectedTabIndex
                                                                }
                                                                onApplyFilter={this.onApplyFilter}
                                                                toggleFilterDialog={
                                                                    this.toggleFilterDialog
                                                                }
                                                                documntDate={documentDate}
                                                                recordUploadedDate={recordUploaded}
                                                            />
                                                        </motion.div>
                                                    </ModalDialog>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex col-span-10 h-full overflow-hidden w-full pt-16 w-full">
                                    <MedicalRecordLeftPanel
                                        encounters={encounters}
                                        selectedEncounter={selectedEncounter}
                                        onEncounterClick={(enc) => {
                                            this.setState({ selectedEncounter: enc });
                                        }}
                                        onAllRecordClick={() => {
                                            this.setState({ selectedEncounter: undefined });
                                        }}
                                    />
                                    <div className="flex flex-1 flex-col items-center justify-center space-y-8">
                                        <div className="w-64 h-64">
                                            <svg
                                                width="64"
                                                height="64"
                                                viewBox="0 0 384 512"
                                                className="fill-neutral-3"
                                            >
                                                <path d="M369.9,97.9,286,14A48,48,0,0,0,252.1-.1H48A48.16,48.16,0,0,0,0,48V464a48,48,0,0,0,48,48H336a48,48,0,0,0,48-48V131.9A48.23,48.23,0,0,0,369.9,97.9Zm-22.6,22.7a15.73,15.73,0,0,1,4.2,7.4H256V32.5a15.73,15.73,0,0,1,7.4,4.2ZM336,480H48a16,16,0,0,1-16-16V48A16,16,0,0,1,48,32H224V136a23.94,23.94,0,0,0,24,24H352V464A16,16,0,0,1,336,480ZM262.71,369.05l-5.66,5.66a12,12,0,0,1-17,0L192,326.63l-48.08,48.08a12,12,0,0,1-17,0l-5.66-5.66a12,12,0,0,1,0-17L169.37,304l-48.08-48.08a12,12,0,0,1,0-17l5.66-5.66a12,12,0,0,1,17,0L192,281.37l48.08-48.08a12,12,0,0,1,17,0l5.66,5.66a12,12,0,0,1,0,17L214.63,304l48.08,48.08A12,12,0,0,1,262.71,369.05Z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="text-neutral-3">
                                                {fetchState === 'error'
                                                    ? 'Error fetching Medical Records. Please try again'
                                                    : fetchState === NO_ACCESS
                                                    ? "You don't have access to this patient's Medical Records"
                                                    : 'No medical records from this patient with applied filters'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )
                    )}
                    {activeTab === REQUEST &&
                        (isABDM ? (
                            <MedicalRecordsTableOld
                                plId={plId}
                                isLoading={isRequestLoading}
                                patientRecords={patientRecords}
                            />
                        ) : (
                            <div className="overflow-auto h-full w-full">
                                <ABDM />
                            </div>
                        ))}
                    {activeTab === DICOM && patientDirectory && (
                        <DicomContainer
                            uuid={patientDirectory.uuid || ''}
                            p_oid={patientDirectory.id || patientDirectory.localId}
                        />
                    )}
                    <RequestMedicalRecords
                        isModalOpen={isModalOpen}
                        closeMe={this.closeRequestMedicalRecords}
                        plId={plId}
                        setActiveTab={this.setActiveTab}
                        patientDirectory={{
                            id: patientDirectory?.localId ?? patientDirectory?.id ?? '',
                            abha: patientDirectory?.abha,
                        }}
                        doctorprofile={this.doctorprofile}
                        onRefresh={this.onRefresh}
                    />
                </div>
            );
        }

        const aggregatedByMonth = list?.reduce((acc, curr) => {
            const date = moment(curr.u_at * 1000).format('YYYY-MM');
            if (!acc[date]) {
                acc[date] = [];
            }
            acc[date].push(curr);
            return acc;
        }, {} as { [key: string]: UIDocumentEntity[] });

        const rId = new URLSearchParams(window.location.search).get('report_document_id');

        return (
            <Fragment>
                <div
                    onClick={() => this.toggleFilterDialog(false)}
                    className="flex flex-col h-full col-span-10 overflow-hidden bg-neutral-8 relative w-full"
                    style={{
                        background: background || '#F4F4F4',
                    }}
                >
                    <div>
                        <NavBar
                            tabs={this.tabs}
                            plId={plId}
                            onRefresh={this.onRefresh}
                            onProfileFilterChange={this.onProfileFilterChange}
                            profileFilter={profileFilterValue}
                            diffCount={diffCount}
                            onRequest={this.requestMedicalRecords}
                            isABDM={isABDM}
                            latestUpdatedAt={latestUpdatedAt}
                            withRefresh={withRefresh}
                            sourceRefreshedAt={sourceRefreshedAt}
                            hideABHACta={hideABHACta}
                        />
                        {!hideSwitchPanel && (
                            <SwitchPanel
                                activeTab={activeTab}
                                setActiveTab={this.setActiveTab}
                                storageInfo={this.props.storageInfo}
                            />
                        )}
                    </div>

                    {activeTab === MY_RECORDS && (
                        <>
                            <div className="flex p-16 w-full space-x-16 w-full items-center">
                                <div className="relative flex justify-end items-center gap-8">
                                    <div
                                        style={{ width: '270px' }}
                                        className="medical-records flex bg-white h-36 border gap-8 w-300 border-darwin-neutral_new-200 rounded-8 items-center justify-between px-12 py-6 leading-5 bg-transparent"
                                    >
                                        {isMrLoading ? (
                                            <svg
                                                className="h-16 fill-darwin-accent-bright-blue-800 animate-spin"
                                                style={{
                                                    width: '17px',
                                                }}
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 512 512"
                                            >
                                                <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                            </svg>
                                        ) : (
                                            <svg
                                                width="17"
                                                height="16"
                                                viewBox="0 0 17 16"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M15.75 14.7188C16.0625 15.0312 16.0625 15.5 15.75 15.7812C15.625 15.9375 15.4375 16 15.25 16C15.0312 16 14.8438 15.9375 14.6875 15.7812L10.5 11.5938C9.375 12.5 7.96875 13 6.46875 13C2.90625 13 0 10.0938 0 6.5C0 2.9375 2.875 0 6.46875 0C10.0312 0 12.9688 2.9375 12.9688 6.5C12.9688 8.03125 12.4688 9.4375 11.5625 10.5312L15.75 14.7188ZM1.5 6.5C1.5 9.28125 3.71875 11.5 6.5 11.5C9.25 11.5 11.5 9.28125 11.5 6.5C11.5 3.75 9.25 1.5 6.5 1.5C3.71875 1.5 1.5 3.75 1.5 6.5Z"
                                                    fill="#767676"
                                                />
                                            </svg>
                                        )}

                                        <input
                                            type="search"
                                            className="medical-records w-full bg-transparent bg-white focus:outline-none placeholder-neutral-5 text-14"
                                            name="ether-search-component"
                                            autoComplete="off"
                                            placeholder="Search In Records"
                                            onChange={(e) =>
                                                this.fetchMedicalRecordsBasedOnSearchDebounce(
                                                    e.target.value,
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                                <div className="h-30 w-1 bg-darwin-neutral-500"></div>
                                <div
                                    style={{ marginLeft: 0 }}
                                    className="flex items-center overflow-hidden hide-scrollbar w-full"
                                >
                                    <div className="flex items-center border-r border-light">
                                        <div
                                            className="px-10 cursor-pointer "
                                                onClick={() => this.onClickScrollHorizontal('left')}
                                            style={{ transform: 'rotate(180deg)' }}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="8"
                                                height="14"
                                                viewBox="0 0 8 14"
                                                fill="none"
                                            >
                                                <path
                                                    d="M7.4082 6.46875C7.68945 6.78125 7.68945 7.25 7.4082 7.53125L1.4082 13.5312C1.0957 13.8438 0.626953 13.8438 0.345703 13.5312C0.0332031 13.25 0.0332031 12.7812 0.345703 12.5L5.81445 7.03125L0.345703 1.53125C0.0332031 1.25 0.0332031 0.78125 0.345703 0.5C0.626953 0.1875 1.0957 0.1875 1.37695 0.5L7.4082 6.46875Z"
                                                    fill="#484848"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                    <div
                                        id={TAGS_ID}
                                        className="flex items-center space-x-8 overflow-x-scroll hide-scrollbar w-full"
                                    >
                                        {Object.keys(tags || {})?.map((key) => {
                                            const isSelected = activeTags?.[key];
                                            return (
                                                <div
                                                    className={`${
                                                        isSelected
                                                            ? 'font-700 bg-darwin-accent-bright-blue-100 text-darwin-accent-bright-blue-800 border-darwin-accent-bright-blue-800'
                                                            : 'bg-white text-darwin-neutral-1000 border-darwin-neutral_new-200'
                                                    } flex items-center space-x-8 whitespace-nowrap text-13 px-12 py-8 rounded-8 border cursor-pointer`}
                                                    onClick={() => this.setActiveTags(key)}
                                                >
                                                    <span>
                                                        {(
                                                            OPTIONS?.find((o) => o.value === key)
                                                                ?.label || key
                                                        )?.replace(/^RT#/, '')}{' '}
                                                        ({tags?.[key]})
                                                    </span>
                                                    {isSelected && (
                                                        <span
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                this.removeActiveTags(key);
                                                            }}
                                                        >
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                width="11"
                                                                height="12"
                                                                viewBox="0 0 11 12"
                                                                fill="none"
                                                            >
                                                                <path
                                                                    d="M10.6582 2.28125L6.9082 6.03125L10.627 9.75C10.9395 10.0312 10.9395 10.5 10.627 10.7812C10.3457 11.0938 9.87695 11.0938 9.5957 10.7812L5.8457 7.0625L2.12695 10.7812C1.8457 11.0938 1.37695 11.0938 1.0957 10.7812C0.783203 10.5 0.783203 10.0312 1.0957 9.71875L4.81445 6L1.0957 2.28125C0.783203 2 0.783203 1.53125 1.0957 1.21875C1.37695 0.9375 1.8457 0.9375 2.1582 1.21875L5.87695 4.96875L9.5957 1.25C9.87695 0.9375 10.3457 0.9375 10.6582 1.25C10.9395 1.53125 10.9395 2 10.6582 2.28125Z"
                                                                    fill="#1A1A1A"
                                                                />
                                                            </svg>
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="flex items-center border-l border-light">
                                        <div
                                            className="px-10 cursor-pointer"
                                            onClick={() => this.onClickScrollHorizontal('right')}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="8"
                                                height="14"
                                                viewBox="0 0 8 14"
                                                fill="none"
                                            >
                                                <path
                                                    d="M7.4082 6.46875C7.68945 6.78125 7.68945 7.25 7.4082 7.53125L1.4082 13.5312C1.0957 13.8438 0.626953 13.8438 0.345703 13.5312C0.0332031 13.25 0.0332031 12.7812 0.345703 12.5L5.81445 7.03125L0.345703 1.53125C0.0332031 1.25 0.0332031 0.78125 0.345703 0.5C0.626953 0.1875 1.0957 0.1875 1.37695 0.5L7.4082 6.46875Z"
                                                    fill="#484848"
                                                />
                                            </svg>
                                        </div>
                                        <div
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                this.toggleFilterDialog(true);
                                            }}
                                            className={`flex items-center justify-end space-x-8 cursor-pointer text-13 px-12 py-8 rounded-8 bg-white font-700 ${
                                                isFilterApplied
                                                    ? 'bg-darwin-accent-bright-blue-100 border border-darwin-accent-bright-blue-800'
                                                    : 'border border-darwin-neutral_new-200'
                                            }`}
                                        >
                                            <div
                                                className={`w-16 h-16 flex items-center justify-center`}
                                            >
                                                {isFilterApplied ? (
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg   "
                                                        viewBox="0 0 512 512"
                                                        className="fill-darwin-accent-bright-blue-800"
                                                    >
                                                        <path d="M3.9 54.9C10.5 40.9 24.5 32 40 32H472c15.5 0 29.5 8.9 36.1 22.9s4.6 30.5-5.2 42.5L320 320.9V448c0 12.1-6.8 23.2-17.7 28.6s-23.8 4.3-33.5-3l-64-48c-8.1-6-12.8-15.5-12.8-25.6V320.9L9 97.3C-.7 85.4-2.8 68.8 3.9 54.9z" />
                                                    </svg>
                                                ) : (
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 512 512"
                                                        className="fill-darwin-accent-bright-blue-800 w-16 h-16"
                                                    >
                                                        <path d="M0 73.7C0 50.67 18.67 32 41.7 32H470.3C493.3 32 512 50.67 512 73.7C512 83.3 508.7 92.6 502.6 100L336 304.5V447.7C336 465.5 321.5 480 303.7 480C296.4 480 289.3 477.5 283.6 472.1L191.1 399.6C181.6 392 176 380.5 176 368.3V304.5L9.373 100C3.311 92.6 0 83.3 0 73.7V73.7zM54.96 80L218.6 280.8C222.1 285.1 224 290.5 224 296V364.4L288 415.2V296C288 290.5 289.9 285.1 293.4 280.8L457 80H54.96z"></path>
                                                    </svg>
                                                )}
                                            </div>
                                            <p className="body-1 text-darwin-accent-bright-blue-800">
                                                Filters
                                            </p>

                                            {isFilterDialogVisible && (
                                                <ModalDialog>
                                                    <motion.div
                                                        initial={{ opacity: 0 }}
                                                        exit={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        style={{ zIndex: 9999 }}
                                                        transition={{
                                                            duration: 0.2,
                                                            ease: 'linear',
                                                        }}
                                                        className="font-lato select-none"
                                                    >
                                                        <FilterDialogV2
                                                            activeTags={activeTags}
                                                            tags={tags}
                                                            tabs={this.tabs}
                                                            selectedTabIndex={
                                                                this.state.selectedTabIndex
                                                            }
                                                            onApplyFilter={this.onApplyFilter}
                                                            toggleFilterDialog={
                                                                this.toggleFilterDialog
                                                            }
                                                            documntDate={documentDate}
                                                            recordUploadedDate={recordUploaded}
                                                        />
                                                    </motion.div>
                                                </ModalDialog>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex col-span-10 h-full overflow-hidden w-full pt-16">
                                <MedicalRecordLeftPanel
                                    encounters={encounters}
                                    selectedEncounter={selectedEncounter}
                                    onEncounterClick={(enc) => {
                                        this.setState({ selectedEncounter: enc });
                                    }}
                                    onAllRecordClick={() => {
                                        this.setState({ selectedEncounter: undefined });
                                    }}
                                />
                                <div className="px-24 pb-16 flex-1 space-y-24 overflow-auto">
                                    <MedicalRecordsVisitHeader
                                        selectedEncounter={selectedEncounter}
                                    />
                                    {Object.keys(aggregatedByMonth)
                                        .sort((a, b) => {
                                            return moment(a).isAfter(moment(b)) ? -1 : 1;
                                        })
                                        .map((groupDate) => (
                                            <div>
                                                <p className="caption-1 bold text-darwin-neutral-1000 mb-12">
                                                    {moment(groupDate).format('MMM YYYY')}
                                                </p>
                                                <div className="grid grid-cols-3 gap-16">
                                                    {aggregatedByMonth[groupDate].map(
                                                        (documentFolder) => {
                                                            const isSelected = Object.keys(
                                                                activeTags || {},
                                                            )?.length
                                                                ? documentFolder?.user_tags?.find(
                                                                      (key) => {
                                                                          return activeTags?.[key];
                                                                      },
                                                                  ) ||
                                                                  documentFolder.user_tags?.find(
                                                                      (t) => t.startsWith('RT#'),
                                                                  )
                                                                    ? true
                                                                    : activeTags?.[
                                                                          documentFolder?.latest_document_type ||
                                                                              ''
                                                                      ]
                                                                : true;

                                                            if (!isSelected) {
                                                                return null;
                                                            }

                                                            return (
                                                                <MedicalRecordNew
                                                                    onDelete={() =>
                                                                        this.onRecordDelete(
                                                                            documentFolder,
                                                                        )
                                                                    }
                                                                    documentFolder={documentFolder}
                                                                    copyToPrescription={
                                                                        this.copyToPrescription
                                                                    }
                                                                    showPtName={
                                                                        !ptIds?.has(
                                                                            documentFolder.oid ||
                                                                                '',
                                                                        )
                                                                    }
                                                                    isSelected={
                                                                        documentFolder.document_id ===
                                                                        rId
                                                                    }
                                                                    hideCopyToPrescription={
                                                                        hideCopyToPrescription
                                                                    }
                                                                />
                                                            );
                                                        },
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        </>
                    )}
                    {activeTab === REQUEST &&
                        (isABDM ? (
                            <MedicalRecordsTableOld
                                plId={plId}
                                isLoading={isRequestLoading}
                                patientRecords={patientRecords}
                            />
                        ) : (
                            <div className="overflow-auto h-full w-full">
                                <ABDM />
                            </div>
                        ))}
                    {activeTab === DICOM && patientDirectory && (
                        <DicomContainer
                            uuid={patientDirectory.uuid || ''}
                            p_oid={patientDirectory.id || patientDirectory.localId}
                        />
                    )}
                </div>
                <RequestMedicalRecords
                    isModalOpen={isModalOpen}
                    closeMe={this.closeRequestMedicalRecords}
                    plId={plId}
                    setActiveTab={this.setActiveTab}
                    patientDirectory={{
                        id: patientDirectory?.localId ?? patientDirectory?.id ?? '',
                        abha: patientDirectory?.abha,
                    }}
                    doctorprofile={this.doctorprofile}
                    onRefresh={this.onRefresh}
                />
            </Fragment>
        );
    }
}

const NavBar: React.FC<{
    onRefresh: () => void;
    plId: string;
    tabs: { label: string }[];
    diffCount?: number;
    profileFilter?: 'profile' | 'all';
    onProfileFilterChange?: (profile: 'profile' | 'all') => void;
    onRequest?: () => void;
    isABDM?: boolean;
    latestUpdatedAt?: string;
    withRefresh?: boolean;
    sourceRefreshedAt?: number;
    hideABHACta?: boolean;
}> = ({
    onRefresh,
    plId,
    tabs,
    diffCount,
    profileFilter,
    onProfileFilterChange,
    onRequest,
    isABDM,
    latestUpdatedAt,
    withRefresh,
    sourceRefreshedAt,
    hideABHACta,
}) => {
    return (
        <div className="h-72 flex-col lg:flex-row px-16 py-12 flex items-center justify-between bg-white border-b border-light">
            <div className="flex items-center space-x-8">
                <div>
                    <div className="flex items-center space-x-8 whitespace-nowrap text-20 text-darwin-neutral-1000">
                        <p>Medical Records</p>
                        {!sourceRefreshedAt && (
                            <RefreshButton onRefresh={onRefresh} withRefresh={withRefresh} />
                        )}
                    </div>
                    {latestUpdatedAt && (
                        <p className="text-13 text-darwin-neutrals-600">
                            Latest Document: {latestUpdatedAt}
                        </p>
                    )}
                </div>
            </div>

            {sourceRefreshedAt && (
                <div className="flex items-center space-x-8 whitespace-nowrap">
                    <p className="text-13 text-darwin-neutrals-600">
                        Last fetched: {formatDistanceToNow(new Date(sourceRefreshedAt * 1000))} ago
                    </p>
                    <RefreshButton onRefresh={onRefresh} withRefresh={withRefresh} />
                </div>
            )}

            <div className="flex items-center space-x-16">
                {diffCount && diffCount > 0 && onProfileFilterChange && profileFilter ? (
                    <div
                        className="secondary-btn-v3 select-none text-13"
                        style={{
                            padding: '6.75px 8px',
                        }}
                    >
                        <Checkbox
                            onChange={(e) =>
                                onProfileFilterChange(e.target.checked ? 'all' : 'profile')
                            }
                            checked={profileFilter === 'all'}
                        >
                            +{diffCount} records from other profiles
                        </Checkbox>
                    </div>
                ) : null}
                {isABDM && !hideABHACta ? (
                    <RequestRecordsBt
                        text={isABDM ? 'Request ABHA' : 'Request via Whatsapp'}
                        className="secondary-btn-v3 px-11 py-7 btn--small disabled"
                        onClick={onRequest}
                    />
                ) : null}

                <AddRecordsBt ptId={plId} onComplete={onRefresh} />
            </div>
        </div>
    );
};

export const LastUpdatedRefreshStrip: React.FC<{
    lastUpdated: string;
    onRefresh: () => void;
    hideLastUpdatedText?: boolean;
}> = ({ lastUpdated, onRefresh, hideLastUpdatedText = false }) => {
    return (
        <div className="flex justify-between p-4 select-none items-center space-x-16">
            {!hideLastUpdatedText && (
                <p className="text-13 text-darwin-neutrals-600">
                    Last updated at {moment(lastUpdated).format('hh:mm A')}
                </p>
            )}

            <div className="flex space-x-8 items-center">
                <button
                    onClick={onRefresh}
                    className="flex space-x-8 items-center cursor-pointer text-14"
                >
                    <svg
                        width="12"
                        height="12"
                        className="fill-darwin-accent-bright-blue-800"
                        viewBox="0 0 512 512"
                    >
                        <path d="M370.72 133.28C339.458 104.008 298.888 87.962 255.848 88C178.39 88.068 111.52 141.178 93.057 214.85C91.713 220.213 86.935 224 81.406 224H24.103C16.605 224 10.909 217.193 12.296 209.824C33.933 94.924 134.813 8 256 8C322.448 8 382.791 34.136 427.315 76.685L463.03 40.97C478.149 25.851 504 36.559 504 57.941V192C504 205.255 493.255 216 480 216H345.941C324.559 216 313.851 190.149 328.97 175.029L370.72 133.28V133.28ZM32 296H166.059C187.441 296 198.149 321.851 183.03 336.971L141.28 378.721C172.542 407.994 213.115 424.04 256.156 424.001C333.574 423.931 400.471 370.857 418.943 297.152C420.287 291.789 425.065 288.002 430.594 288.002H487.898C495.396 288.002 501.092 294.809 499.705 302.178C478.067 417.076 377.187 504 256 504C189.552 504 129.209 477.864 84.685 435.315L48.97 471.03C33.851 486.149 8 475.441 8 454.059V320C8 306.745 18.745 296 32 296Z"></path>
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default connect((state: GlobalState) => ({
    isLabVitalsEnabled: getPxElementConfig(state).find((t) => t.id === 'labVitals')?.isShown,
    isABDM: getGenieSetting(state, 'abdm') as boolean,
    storageInfo: getIsStorageInfo(state),
}))(MedicalRecordsRightPanel);

const AddRecordsBt: React.FC<{ ptId: string; onComplete?: () => void }> = (props) => {
    const { ptId } = props;
    const [files, setFiles] = useState<File[]>([]);
    const [dialogVisible, setDialogVisibility] = useState(false);

    const onFilesSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) {
            return;
        }

        setFiles(Array.from(e.target.files));
        setDialogVisibility(true);
    };

    const closeDialog = () => {
        setFiles([]);
        setDialogVisibility(false);
        props.onComplete && props.onComplete();
    };

    return (
        <Fragment>
            <button
                className={`primary-btn-v3 btn--small space-x-8 ${getAclViewClassName(
                    SREV2.UPLOAD_MEDICAL_RECORDS,
                )}`}
            >
                <label
                    // for="file-upload"
                    className="w-full px-12 py-8 cursor-pointer"
                >
                    <span className="flex items-center justify-center w-full space-x-8">
                        <p className="text-13">Upload</p>
                        <div className="flex items-center justify-center w-16 h-16">
                            {uploadMRIcon}
                        </div>
                    </span>
                    <input
                        type="file"
                        name="file"
                        accept={SUPPORTED_MR_FORMATS}
                        id={`file-upload-${randomString(4)}`}
                        className="hidden"
                        onChange={onFilesSelect}
                        onError={console.error}
                        multiple
                    />
                </label>
            </button>

            <AnimatePresence>
                {dialogVisible && (
                    <UploadDialog
                        key={ptId}
                        files={files}
                        close={closeDialog}
                        setFiles={setFiles}
                        ptId={ptId}
                    />
                )}
            </AnimatePresence>
        </Fragment>
    );
};

export const AddRecordsBtV2: React.FC<{
    ptId: string;
    onComplete?: () => void;
    icon?: JSX.Element;
    cssClass?: string;
    cssClassInner?: string;
    text: string;
    from?: string;
    showArrow?: boolean;
    closeDropdown?: () => void;
}> = (props) => {
    const { ptId, icon, cssClass, text, cssClassInner, from, showArrow, closeDropdown } = props;
    const [files, setFiles] = useState<File[]>([]);
    const [dialogVisible, setDialogVisibility] = useState(false);

    const onFilesSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) {
            return;
        }

        track('Medical_Record_File_Select', { from, fileNos: e.target.files.length.toString() });

        setFiles(Array.from(e.target.files));
        setDialogVisibility(true);
    };

    const closeDialog = () => {
        setFiles([]);
        setDialogVisibility(false);
        props.onComplete && props.onComplete();
    };

    const handleClick = () => {
        closeDropdown && closeDropdown();
        track('Add Medical Records', { from });
    };

    return (
        <Fragment>
            <button
                className={`
                  ${
                      cssClass ||
                      'flex cursor-pointer items-center  font-700 text-14 border border-darwin-primary-500 p-8 px-20 m-auto rounded-4 text-darwin-primary-500'
                  } ${getAclViewClassName(SREV2.UPLOAD_MEDICAL_RECORDS)}
                `}
                onClick={handleClick}
            >
                <label
                    className={
                        cssClassInner ||
                        'flex h-full w-full items-center space-x-8 justify-center cursor-pointer'
                    }
                >
                    {icon && <span>{icon}</span>}
                    <span>{text}</span>
                    <input
                        type="file"
                        name="file"
                        accept={SUPPORTED_MR_FORMATS}
                        id={`file-upload-${randomString(4)}`}
                        className="hidden"
                        onChange={onFilesSelect}
                        onError={console.error}
                        multiple
                    />
                    {showArrow && (
                        <svg
                            width="25"
                            height="25"
                            viewBox="0 0 28 28"
                            className="fill-neutral-5 flex-shrink-0 cursor-pointer"
                            preserveAspectRatio="xMidYMid meet"
                        >
                            <path d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z"></path>
                        </svg>
                    )}
                </label>
            </button>

            <AnimatePresence>
                {dialogVisible && (
                    <UploadDialog
                        key={ptId}
                        files={files}
                        close={closeDialog}
                        setFiles={setFiles}
                        ptId={ptId}
                    />
                )}
            </AnimatePresence>
        </Fragment>
    );
};

const fuseConfig = { keys: ['name', 's_name'], threshold: 0.2 };

export const UploadDialog: React.FC<{
    files: File[];
    close: () => void;
    setFiles: (files: File[]) => void;
    ptId: string;
}> = ({ files, close, setFiles, ptId }) => {
    const [apiState, setApiState] = useState<'' | 'loading' | 'success' | 'error'>('');
    const [documentType, setDocumentType] = useState<
        ({ label?: string; value?: string; tg?: string[] } | undefined)[]
    >([] as { label?: string; value?: string; tg?: string[] }[]);
    const [documentDates, setDocumentDates] = useState<string[]>([]);
    const [tagsGElements, setTagsGElements] = useState<GMedicalRecordsWithTrack[] | undefined>();

    const fetchAndStoreTagsSuggestions = async () => {
        const medicalRecordsTags = await getGArray<GMedicalRecordsTags>(
            getMActiveId() || '',
            DB_TABLES['medicalRecordsTags'],
        );
        setTagsGElements(medicalRecordsTags);
    };

    useEffect(() => {
        fetchAndStoreTagsSuggestions();
    }, []);

    const [uploadedCount, setUploadeCount] = useState(0);

    const upload = async () => {
        setApiState('loading');
        setUploadeCount(0);
        track('Save_Medical_Records', { from: 'UploadDialog' });

        onSaveRecordsDetailsInGenie();

        const pt = await getPatientByLocalId(ptId);

        files.forEach((file, index) => {
            const dt = documentType[index]?.value;
            if (!dt) {
                // not gonna happen, button is disabled till then
                alert('Please enter valid document type');
                return;
            }

            const rb = {
                dt: dt,
                files: [{ contentType: file.type }],
                dd: documentDates[index],
                sh: true,
                patient_oid: ptId,
                tg: documentType[index]?.tg,
                p_uuid: pt.uuid,
                p_poid: pt.poid,
            };

            initiateFileUpload(rb)
                .then((res) => {
                    if (res.error || !res.forms || res.forms.length !== 1) {
                        setApiState('error');
                        alert(res.message || 'Failed to upload documents, please try again');
                        return;
                    }

                    const form = res.forms[0];

                    const formData = new FormData();

                    Object.entries(form.fields).map(([k, v]) => {
                        formData.append(k, v);
                    });

                    formData.append('file', file);

                    const start = new Date().getTime();

                    fetch(form.url, { method: 'POST', body: formData })
                        .then(() => {
                            track('MR_UPLOAD_TIME', {
                                uploadTimeInMS: `${new Date().getTime() - start}`,
                                status: 'success',
                                fileType: file.type,
                                fileSize: `${file.size}`,
                                fileName: file.name,
                            });

                            setUploadeCount((c) => {
                                if (c + 1 === files.length) {
                                    // delay refresh so that BE get's ready
                                    setTimeout(() => {
                                        close();
                                    }, 0);
                                }
                                return c + 1;
                            });
                        })
                        .catch((e) => {
                            captureError(e);
                            console.log(file.name);

                            track('MR_UPLOAD_TIME', {
                                uploadTimeInMS: `${new Date().getTime() - start}`,
                                status: 'error',
                                fileType: file.type,
                                fileSize: `${file.size}`,
                                fileName: file.name,
                                error: e.message,
                                ptId,
                            });

                            alert('Failed to upload: ' + file.name);
                        });
                })
                .catch((e) => {
                    captureError(e);
                    setApiState('error');
                    alert('Failed to upload documents, please try again');
                    track('MR_UPLOAD_TIME', {
                        // uploadTimeInMS: `${new Date().getTime() - start}`,
                        status: 'error',
                        fileType: file.type,
                        fileSize: `${file.size}`,
                        fileName: file.name,
                        error: e.message,
                        ptId,
                    });
                });
        });

        return;
    };

    const onDelete = (index: number) => {
        files.splice(index, 1);
        documentType.splice(index, 1);
        setFiles([...files]);
        setDocumentType([...documentType]);
    };

    const onDocumentDateChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        documentDates[index] = e.target.value;
        setDocumentDates(Array.from(documentDates));
    };

    const onCopyDuration = (mrIndex: number) => {
        documentType[mrIndex] &&
            setDocumentType(new Array(files.length).fill(documentType[mrIndex]));
        setDocumentDates(new Array(files.length).fill(documentDates[mrIndex]));

        track('COPY_TO_ALL', { what: 'MR_ROW' });
    };

    const onTagsChange = (index: number, tags: string[]) => {
        documentType[index] = {
            ...(documentType[index] || {}),
            tg: uniq([
                ...(documentType[index]?.tg?.filter((tag) => tag?.startsWith('RT#')) || []),
                ...tags,
            ]),
        };

        setDocumentType([...documentType]);
    };

    const onAddRTTagsChange = (index: number, rtTag: string) => {
        if (rtTag) {
            documentType[index] = {
                ...(documentType[index] || {}),
                tg: uniq([
                    ...(documentType[index]?.tg?.filter((tag) => !tag?.startsWith('RT#')) || []),
                    rtTag,
                ]),
            };

            setDocumentType([...documentType]);
        }
    };

    const onClearRTTagsChange = (index: number) => {
        if (documentType[index]?.tg?.find((tag) => tag?.startsWith('RT#'))) {
            documentType[index] = {
                ...(documentType[index] || {}),
                tg: documentType[index]?.tg?.filter((tag) => !tag?.startsWith('RT#')),
            };
            setDocumentType([...documentType]);
        }
    };

    const getTagsSuggestions = () => {
        const data =
            tagsGElements
                ?.filter((tag) => !tag?.name?.startsWith('RT#'))
                ?.map((tag) => {
                    return {
                        value: tag.name,
                        label: tag.name,
                    };
                }) || [];

        return data;
    };

    const onSaveRecordsDetailsInGenie = async () => {
        const genieTags = documentType
            .filter((doc) => doc?.tg)
            .reduce((prev, pres) => {
                return [
                    ...(prev || []),
                    ...(pres?.tg?.map((t) => ({
                        id: `${LOCAL_ID_PREFIX}${hashCode(t.trim())}`,
                        name: t,
                    })) || []),
                ];
            }, [] as any);
        await updateG(getMActiveId() || '', DB_TABLES.medicalRecordsTags, genieTags);
        await pushGenieToBE(getMActiveId() || '', ['medicalRecordsTags']);
    };

    const onFileTypeSelect = (index: number) => (
        item?: {
            label: string;
            value: string;
            tags?: string;
        } | null,
    ): void => {
        if (item) {
            documentType[index] = { ...(documentType[index] || {}), ...item };
        } else {
            documentType[index] = undefined;
        }

        setDocumentType([...documentType]);
    };

    return (
        <ModalDialog>
            <motion.div
                initial={{ opacity: 0 }}
                exit={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{ zIndex: 10 }}
                className="font-lato select-none fixed"
            >
                <div
                    onClick={apiState !== 'loading' ? close : undefined}
                    className="modal-overlay"
                ></div>
                <div className="flex flex-col modal-content w-50p h-2/3">
                    {apiState === 'loading' && (
                        <div className="absolute inset-0">
                            <Loader text={`Uploading ${uploadedCount}/${files.length}`} />
                        </div>
                    )}
                    <div className="flex items-center justify-between px-16 py-12 border-b bg-neutral-9 rounded-t-4 text-neutral-black font-700 border-neutral-7">
                        <p>Upload a medical record</p>
                        <div
                            onClick={close}
                            className="flex items-center justify-center w-16 h-16 cursor-pointer"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 320 512"
                                className="fill-darwin-neutral-1000 w-16 h-16"
                            >
                                <path d="M312.1 375c9.369 9.369 9.369 24.57 0 33.94s-24.57 9.369-33.94 0L160 289.9l-119 119c-9.369 9.369-24.57 9.369-33.94 0s-9.369-24.57 0-33.94L126.1 256L7.027 136.1c-9.369-9.369-9.369-24.57 0-33.94s24.57-9.369 33.94 0L160 222.1l119-119c9.369-9.369 24.57-9.369 33.94 0s9.369 24.57 0 33.94L193.9 256L312.1 375z" />
                            </svg>
                        </div>
                    </div>
                    <div className="flex-1 p-16 space-y-16 overflow-auto bg-neutral-9">
                        {files.map((f, index) => {
                            return (
                                <div
                                    key={`${f.name}-${index}`}
                                    className="bg-white rounded-8"
                                    id={`mr-row-${index}`}
                                >
                                    <div className="mr-row">
                                        <div className="flex items-center justify-between px-16 py-10 border-b border-darwin-neutral-100">
                                            <p className="body-3 text-darwin-neutral-1000">
                                                {f.name}
                                            </p>
                                            <div
                                                onClick={() => onDelete(index)}
                                                className="flex items-center space-x-8 cursor-pointer"
                                            >
                                                <div className="flex items-center justify-center w-16 h-16">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 448 512"
                                                        className="fill-darwin-status-warning-500 w-14 h-14"
                                                    >
                                                        <path d="M424 80C437.3 80 448 90.75 448 104C448 117.3 437.3 128 424 128H412.4L388.4 452.7C385.9 486.1 358.1 512 324.6 512H123.4C89.92 512 62.09 486.1 59.61 452.7L35.56 128H24C10.75 128 0 117.3 0 104C0 90.75 10.75 80 24 80H93.82L130.5 24.94C140.9 9.357 158.4 0 177.1 0H270.9C289.6 0 307.1 9.358 317.5 24.94L354.2 80H424zM177.1 48C174.5 48 171.1 49.34 170.5 51.56L151.5 80H296.5L277.5 51.56C276 49.34 273.5 48 270.9 48H177.1zM364.3 128H83.69L107.5 449.2C108.1 457.5 115.1 464 123.4 464H324.6C332.9 464 339.9 457.5 340.5 449.2L364.3 128z" />
                                                    </svg>
                                                </div>
                                                <p className="body-1 bold text-darwin-status-warning-500">
                                                    Delete file
                                                </p>
                                            </div>
                                        </div>
                                        <div className="relative flex items-center justify-between pl-16 pr-24 py-12 mr-upload">
                                            <div>
                                                <EmbedMedia file={f} />
                                            </div>
                                            <div className="w-25p space-y-8">
                                                <p className="body-1 bold text-darwin-neutral-1000">
                                                    Report Type
                                                </p>
                                                <MedicalRecordFileTypeSelector
                                                    onChange={(data) => {
                                                        if (data?.value?.startsWith('RT#')) {
                                                            onFileTypeSelect(index)({
                                                                label: 'Others',
                                                                value: 'ot',
                                                            });

                                                            onAddRTTagsChange(index, data.value);
                                                        } else {
                                                            onFileTypeSelect(index)(data);
                                                            onClearRTTagsChange(index);
                                                        }
                                                    }}
                                                    value={
                                                        documentType[index]?.value
                                                            ? (documentType[
                                                                  index
                                                              ] as SuggestionItem)
                                                            : undefined
                                                    }
                                                    tagsGElements={tagsGElements || []}
                                                />
                                            </div>
                                            <div className="w-20p space-y-8">
                                                <p className="body-1 bold text-darwin-neutral-1000">
                                                    Investigation Date
                                                </p>
                                                <div className="">
                                                    <input
                                                        type={'date'}
                                                        max={moment().format('YYYY-MM-DD')}
                                                        placeholder={'Document Date'}
                                                        value={documentDates[index]}
                                                        onChange={onDocumentDateChange(index)}
                                                        className="search-input px-4 py-7 text-neutral-black text-14 border-darwin-neutral-200 border"
                                                    ></input>
                                                    <div className="mimic-box"></div>
                                                </div>
                                            </div>
                                            {files.length > 1 && (
                                                <CopyToAll
                                                    id={`copy-duration-v2-mr-svg-${index}`}
                                                    onClick={() => onCopyDuration(index)}
                                                    tableId={`mr-row-${index}`}
                                                    className="top-6 right-6"
                                                />
                                            )}

                                            <div className="w-30p space-y-8">
                                                <p className="body-1 bold text-darwin-neutral-1000">
                                                    Report Tags
                                                </p>

                                                <div className="">
                                                    <Select
                                                        mode="tags"
                                                        style={{ width: '100%' }}
                                                        placeholder="Critical, Dengue..."
                                                        onChange={(values) => {
                                                            onTagsChange(index, values);
                                                        }}
                                                        notFoundContent={null}
                                                        options={getTagsSuggestions()}
                                                    />
                                                </div>
                                            </div>
                                            {files.length > 1 && (
                                                <CopyToAll
                                                    id={`copy-duration-v2-mr-svg-${index}`}
                                                    onClick={() => onCopyDuration(index)}
                                                    tableId={`mr-row-${index}`}
                                                    className="top-6 right-6"
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="flex items-center justify-between px-16 py-8 space-x-16 border-t rounded-b-4 border-neutral-7">
                        <div>
                            <label
                                // for="file-upload"
                                className="px-16 py-8 btn--small secondary-btn cursor-pointer space-x-8"
                            >
                                <div className="flex items-center justify-center w-16 h-16">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 448 512"
                                        className="w-16 h-16 fill-actionPrimary"
                                    >
                                        <path d="M429.3 93.38l-74.63-74.64C342.6 6.742 326.3 0 309.4 0H160C124.7 0 96 28.65 96 64l.0098 288c0 35.34 28.65 64 64 64h224C419.2 416 448 387.2 448 352V138.6C448 121.7 441.3 105.4 429.3 93.38zM400 352c0 8.836-7.164 16-16 16H160c-8.838 0-16-7.164-16-16L144 64.13c0-8.836 7.164-16 16-16h128V128c0 17.67 14.33 32 32 32h79.99V352zM328 512h-208C53.83 512 0 458.2 0 392v-272C0 106.8 10.75 96 24 96S48 106.8 48 120v272c0 39.7 32.3 72 72 72h208c13.25 0 24 10.75 24 24S341.3 512 328 512z" />
                                    </svg>
                                </div>
                                <span>Upload another record</span>
                                <input
                                    type="file"
                                    name="file"
                                    accept={SUPPORTED_MR_FORMATS}
                                    id="file-upload"
                                    className="hidden"
                                    onChange={(e) =>
                                        e.target.files &&
                                        setFiles([...files, ...Array.from(e.target.files)])
                                    }
                                    multiple
                                />
                            </label>
                        </div>
                        <button
                            disabled={
                                files.length === 0 ||
                                documentType.filter((dt) => dt).length !== files.length ||
                                documentType.filter((dt) => dt?.label && dt.value).length !==
                                    files.length
                            }
                            onClick={upload}
                            className="px-16 py-8 btn--small primary-btn space-x-8"
                        >
                            <div className="flex items-center justify-center w-16 h-16">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 448 512"
                                    className="fill-white w-16 h-16"
                                >
                                    <path d="M224 256c-35.2 0-64 28.8-64 64c0 35.2 28.8 64 64 64c35.2 0 64-28.8 64-64C288 284.8 259.2 256 224 256zM433.1 129.1l-83.9-83.9C341.1 37.06 328.8 32 316.1 32H64C28.65 32 0 60.65 0 96v320c0 35.35 28.65 64 64 64h320c35.35 0 64-28.65 64-64V163.9C448 151.2 442.9 138.9 433.1 129.1zM128 80h144V160H128V80zM400 416c0 8.836-7.164 16-16 16H64c-8.836 0-16-7.164-16-16V96c0-8.838 7.164-16 16-16h16v104c0 13.25 10.75 24 24 24h192C309.3 208 320 197.3 320 184V83.88l78.25 78.25C399.4 163.2 400 164.8 400 166.3V416z" />
                                </svg>
                            </div>
                            <span>Save</span>
                        </button>
                    </div>
                </div>
            </motion.div>
        </ModalDialog>
    );
};

const Loader: React.FC<{ text: string }> = ({ text }) => {
    return (
        <Fragment>
            <div style={{ backgroundColor: 'rgba(0,0,0,0.1)' }} className="modal-overlay"></div>
            <div className="flex flex-col items-center justify-center h-full w-full bh-white rounded-16 space-y-8">
                <div
                    className="flex flex-col bg-white rounded-16 space-y-8 p-16 items-center"
                    style={{ zIndex: 900 }}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        className="w-24 h-24 animate-spin fill-actionPrimary"
                    >
                        <path d="M288 28.977v16.391c0 7.477 5.182 13.945 12.474 15.598C389.568 81.162 456 160.742 456 256c0 110.532-89.451 200-200 200-110.532 0-200-89.451-200-200 0-95.244 66.422-174.837 155.526-195.034C218.818 59.313 224 52.845 224 45.368V28.981c0-10.141-9.322-17.76-19.246-15.675C91.959 37.004 7.373 137.345 8.004 257.332c.72 137.052 111.477 246.956 248.531 246.667C393.255 503.711 504 392.788 504 256c0-119.349-84.308-219.003-196.617-242.665C297.403 11.232 288 18.779 288 28.977z" />
                    </svg>

                    <span className="">{text}</span>
                </div>
            </div>
        </Fragment>
    );
};

const EmbedMedia: React.FC<{ file: File }> = ({ file }) => {
    const onClick = () => {
        track('EMBED_OPEN', { type: file.type });
        window.open(URL.createObjectURL(file));
    };

    useEffect(() => {
        const output = document.getElementById(file.name) as HTMLImageElement;
        if (!output) return;
        output.src = URL.createObjectURL(file);
        output.onload = function () {
            URL.revokeObjectURL(output.src); // free memory
        };
    }, []);

    return IMAGE_TYPE.includes(file.type) ? (
        <div
            onClick={onClick}
            className="cursor-pointer w-80 h-80  bg-background-inputBG rounded-8"
        >
            <img className="w-full h-full rounded-8" id={file.name} />
        </div>
    ) : AUDIO_TYPE.includes(file.type) ? (
        <div
            onClick={onClick}
            className="cursor-pointer w-80 h-80 flex flex-col items-center bg-background-inputBG rounded-8 p-8"
        >
            <div className="flex items-center justify-center w-full h-full">
                <svg
                    width="31"
                    height="32"
                    viewBox="0 0 31 32"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        opacity="0.2"
                        d="M15.2241 31.2416C14.9203 31.2416 14.6289 31.1259 14.4141 30.92C14.1992 30.7141 14.0785 30.4348 14.0785 30.1437V1.59791C14.0785 1.30673 14.1992 1.02747 14.4141 0.821571C14.6289 0.615673 14.9203 0.5 15.2241 0.5C15.5279 0.5 15.8193 0.615673 16.0341 0.821571C16.249 1.02747 16.3697 1.30673 16.3697 1.59791V30.1437C16.3697 30.4348 16.249 30.7141 16.0341 30.92C15.8193 31.1259 15.5279 31.2416 15.2241 31.2416ZM22.0975 24.6541C21.7937 24.6541 21.5023 24.5384 21.2875 24.3325C21.0726 24.1266 20.9519 23.8474 20.9519 23.5562V8.18539C20.9519 7.89421 21.0726 7.61495 21.2875 7.40905C21.5023 7.20315 21.7937 7.08748 22.0975 7.08748C22.4013 7.08748 22.6927 7.20315 22.9075 7.40905C23.1224 7.61495 23.2431 7.89421 23.2431 8.18539V23.5562C23.2431 23.8474 23.1224 24.1266 22.9075 24.3325C22.6927 24.5384 22.4013 24.6541 22.0975 24.6541ZM28.9709 20.2624C28.6671 20.2624 28.3757 20.1468 28.1609 19.9409C27.946 19.735 27.8253 19.4557 27.8253 19.1645V12.577C27.8253 12.2859 27.946 12.0066 28.1609 11.8007C28.3757 11.5948 28.6671 11.4791 28.9709 11.4791C29.2747 11.4791 29.5661 11.5948 29.7809 11.8007C29.9958 12.0066 30.1165 12.2859 30.1165 12.577V19.1645C30.1165 19.4557 29.9958 19.735 29.7809 19.9409C29.5661 20.1468 29.2747 20.2624 28.9709 20.2624ZM8.35071 24.6541C8.04689 24.6541 7.75551 24.5384 7.54068 24.3325C7.32584 24.1266 7.20515 23.8474 7.20515 23.5562V8.18539C7.20515 7.89421 7.32584 7.61495 7.54068 7.40905C7.75551 7.20315 8.04689 7.08748 8.35071 7.08748C8.65454 7.08748 8.94592 7.20315 9.16075 7.40905C9.37559 7.61495 9.49628 7.89421 9.49628 8.18539V23.5562C9.49628 23.8474 9.37559 24.1266 9.16075 24.3325C8.94592 24.5384 8.65454 24.6541 8.35071 24.6541ZM1.47732 20.2624C1.1735 20.2624 0.88212 20.1468 0.667285 19.9409C0.45245 19.735 0.331757 19.4557 0.331757 19.1645V12.577C0.331757 12.2859 0.45245 12.0066 0.667285 11.8007C0.88212 11.5948 1.1735 11.4791 1.47732 11.4791C1.78114 11.4791 2.07252 11.5948 2.28736 11.8007C2.50219 12.0066 2.62289 12.2859 2.62289 12.577V19.1645C2.62289 19.4557 2.50219 19.735 2.28736 19.9409C2.07252 20.1468 1.78114 20.2624 1.47732 20.2624Z"
                        fill="black"
                    ></path>
                    <path
                        d="M22.766 15.2642L22.2581 14.9538L10.3431 7.42544C9.48362 6.88216 8.35071 7.50305 8.35071 8.55081V24.1896C8.35071 25.1985 9.48362 25.8194 10.3821 25.2762L22.8051 17.4762C23.5864 16.9329 23.5864 15.7687 22.766 15.2642Z"
                        fill="#2D4356"
                    ></path>
                </svg>
            </div>
        </div>
    ) : VIDEO_TYPE.includes(file.type) ? (
        <div
            onClick={onClick}
            className="cursor-pointer h-80 w-80 flex flex-col items-center bg-background-inputBG rounded-8 p-8"
        >
            <div className="flex items-center justify-center w-full h-full">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 384 512"
                    className="w-32 h-32 fill-darwin-neutral-700"
                >
                    <path d="M256 0v128h128L256 0zM224 128L224 0H48C21.49 0 0 21.49 0 48v416C0 490.5 21.49 512 48 512h288c26.51 0 48-21.49 48-48V160h-127.1C238.3 160 224 145.7 224 128zM224 384c0 17.67-14.33 32-32 32H96c-17.67 0-32-14.33-32-32V288c0-17.67 14.33-32 32-32h96c17.67 0 32 14.33 32 32V384zM320 284.9v102.3c0 12.57-13.82 20.23-24.48 13.57L256 376v-80l39.52-24.7C306.2 264.6 320 272.3 320 284.9z" />
                </svg>
            </div>
        </div>
    ) : (
        <div
            onClick={onClick}
            className="cursor-pointer h-80 w-80 flex flex-col items-center bg-background-inputBG rounded-8 p-8"
        >
            <div className="flex items-center justify-center w-full h-full">
                <svg
                    width="28"
                    height="32"
                    viewBox="0 0 28 32"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M2 0H18.993L28 8.972V30C28 31.105 27.104 32 26 32H2C0.896 32 0 31.105 0 30V2C0 0.895 0.896 0 2 0Z"
                        fill="#E2574C"
                    ></path>
                    <path
                        d="M27.971 8.99999H21C19.896 8.99999 19 8.10399 19 6.99999V0.019989L27.971 8.99999Z"
                        fill="#B53629"
                    ></path>
                    <path
                        d="M20.498 15.163C20.833 15.163 20.997 14.871 20.997 14.588C20.997 14.295 20.826 14.012 20.498 14.012H18.59C18.217 14.012 18.009 14.321 18.009 14.662V19.351C18.009 19.769 18.247 20.001 18.569 20.001C18.889 20.001 19.128 19.769 19.128 19.351V18.064H20.282C20.64 18.064 20.819 17.771 20.819 17.48C20.819 17.195 20.64 16.912 20.282 16.912H19.128V15.163H20.498ZM14.049 14.012H12.653C12.274 14.012 12.005 14.272 12.005 14.658V19.355C12.005 19.834 12.392 19.984 12.669 19.984H14.134C15.868 19.984 17.013 18.843 17.013 17.082C17.012 15.22 15.934 14.012 14.049 14.012ZM14.116 18.826H13.265V15.17H14.032C15.193 15.17 15.698 15.949 15.698 17.024C15.698 18.03 15.202 18.826 14.116 18.826ZM9.00199 14.012H7.61899C7.22799 14.012 7.00999 14.27 7.00999 14.662V19.351C7.00999 19.769 7.25999 20.001 7.59599 20.001C7.93199 20.001 8.18199 19.769 8.18199 19.351V17.982H9.04899C10.119 17.982 11.002 17.224 11.002 16.005C11.002 14.812 10.15 14.012 9.00199 14.012ZM8.97899 16.882H8.18199V15.113H8.97899C9.47099 15.113 9.78399 15.497 9.78399 15.998C9.78299 16.498 9.47099 16.882 8.97899 16.882Z"
                        fill="white"
                    ></path>
                </svg>
            </div>
        </div>
    );
};

export const RequestRecordsBt: React.FC<{
    text: string;
    className?: string;
    onClick?: () => void;
    icon?: ReactNode;
}> = ({ text, className, onClick, icon }) => {
    const onClickHandler = () => {
        onClick?.();
        track('REQUEST_MEDICAL_RECORDS');
    };

    return (
        <Fragment>
            <AntTooltip
                placement="bottom"
                content={'Ask Medical Records from patient via Whatsapp.'}
            >
                <button
                    className={`flex items-center space-x-8 text-13 font-700 ${
                        className ?? ''
                    } ${getAclViewClassName(SREV2.CAN_REQUEST_RECORDS)}`}
                    onClick={onClickHandler}
                >
                    <span>{text}</span>
                    {icon ?? (
                        <svg
                            className="w-10 h-14 fill-darwin-accent-bright-blue-800"
                            viewBox="0 0 12 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M6.75 10.7188V7.5C6.75 7.09375 6.40625 6.75 6 6.75C5.5625 6.75 5.25 7.09375 5.25 7.5V10.7188L4 9.46875C3.875 9.34375 3.6875 9.25 3.5 9.25C3.28125 9.25 3.09375 9.34375 2.96875 9.46875C2.65625 9.78125 2.65625 10.25 2.96875 10.5312L5.46875 13.0312C5.75 13.3438 6.21875 13.3438 6.5 13.0312L9 10.5312C9.3125 10.25 9.3125 9.78125 9 9.46875C8.71875 9.1875 8.25 9.1875 7.96875 9.46875L6.75 10.7188ZM11.4062 2.9375L9.0625 0.59375C8.6875 0.21875 8.1875 0 7.65625 0H2C0.875 0 0 0.90625 0 2V14C0 15.125 0.875 16 2 16H10C11.0938 16 12 15.125 12 14V4.34375C12 3.8125 11.7812 3.3125 11.4062 2.9375ZM10.5 14C10.5 14.2812 10.25 14.5 10 14.5H2C1.71875 14.5 1.5 14.2812 1.5 14V2.03125C1.5 1.75 1.71875 1.53125 2 1.53125H7V4C7 4.5625 7.4375 5 8 5H10.4688V14H10.5Z" />
                        </svg>
                    )}
                </button>
            </AntTooltip>
        </Fragment>
    );
};

const RefreshButton: React.FC<{
    onRefresh: () => void;
    withRefresh?: boolean;
}> = ({ onRefresh, withRefresh }) => {
    return (
        <button
            onClick={onRefresh}
            className={`flex space-x-8 items-center cursor-pointer text-14 ${
                withRefresh ? 'disabled:opacity-70 disabled:cursor-not-allowed' : ''
            }`}
            disabled={withRefresh}
        >
            {withRefresh ? (
                <svg
                    className="w-14 h-14 fill-darwin-accent-bright-blue-800 animate-spin"
                    viewBox="0 0 512 512"
                >
                    <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                </svg>
            ) : (
                <svg className="w-14 h-14 fill-darwin-accent-bright-blue-800" viewBox="0 0 512 512">
                    <path d="M370.72 133.28C339.458 104.008 298.888 87.962 255.848 88C178.39 88.068 111.52 141.178 93.057 214.85C91.713 220.213 86.935 224 81.406 224H24.103C16.605 224 10.909 217.193 12.296 209.824C33.933 94.924 134.813 8 256 8C322.448 8 382.791 34.136 427.315 76.685L463.03 40.97C478.149 25.851 504 36.559 504 57.941V192C504 205.255 493.255 216 480 216H345.941C324.559 216 313.851 190.149 328.97 175.029L370.72 133.28V133.28ZM32 296H166.059C187.441 296 198.149 321.851 183.03 336.971L141.28 378.721C172.542 407.994 213.115 424.04 256.156 424.001C333.574 423.931 400.471 370.857 418.943 297.152C420.287 291.789 425.065 288.002 430.594 288.002H487.898C495.396 288.002 501.092 294.809 499.705 302.178C478.067 417.076 377.187 504 256 504C189.552 504 129.209 477.864 84.685 435.315L48.97 471.03C33.851 486.149 8 475.441 8 454.059V320C8 306.745 18.745 296 32 296Z"></path>
                </svg>
            )}
        </button>
    );
};
